import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA, TlkModalWindowModule } from '@itero/ui-toolkit-angular/modal-window';
import { TlkTextInputComponent } from '@itero/ui-toolkit-angular/text-input';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkRadioGroupDirective, TlkRadioButtonComponent } from '@itero/ui-toolkit-angular/radio-button';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';
import { TranslateModule } from '@ngx-translate/core';
import { Patient } from '../../state-machine/patient-forms.types';
import { PatientFormsMachineService } from '../../services/patient-forms-machine.service';
import { DobFormatService } from '../../services/dob-format.service';
import { DobInputComponent } from '../dob-input/dob-input.component';
import { UnifiedPatientsBiService } from '../../observability/bi.service';
import { BI_DIMENSIONS, BI_FEATURE } from '../../observability/bi-constants';

export type PatientEditorMode = 'editingItero' | 'fillingInvisalignFields';

export interface PatientEditorData {
	mode: PatientEditorMode;
	patient: Patient;
}

export type PatientEditorResult =
	| { action: 'saved'; patient: Patient; createInvisalign: boolean }
	| { action: 'savedWithoutInvisalign'; patient: Patient }
	| undefined; // cancelled

@Component({
	selector: 'rx-patient-editor-content',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TlkModalWindowModule,
		TlkTextInputComponent,
		TlkButtonComponent,
		TlkRadioGroupDirective,
		TlkRadioButtonComponent,
		TlkBannerComponent,
		TranslateModule,
		DobInputComponent
	],
	templateUrl: './patient-editor.component.html',
	styleUrls: ['./patient-editor.component.scss']
})
export class PatientEditorContentComponent implements OnDestroy {
	form: { firstName: string; lastName: string; gender: string; chartNumber: string };
	parsedDateOfBirth: string;
	private duplicateBannerShown = false;
	private readonly subscription;

	constructor(
		public modalWindowRef: TlkModalWindowRef<PatientEditorResult>,
		@Inject(TLK_MODAL_WINDOW_DATA) public data: PatientEditorData,
		private readonly machine: PatientFormsMachineService,
		private readonly dobFormat: DobFormatService,
		private readonly bi: UnifiedPatientsBiService
	) {
		this.parsedDateOfBirth = data.patient.dateOfBirth || '';
		this.form = {
			firstName: data.patient.firstName,
			lastName: data.patient.lastName,
			gender: data.patient.gender,
			chartNumber: data.patient.chartNumber
		};

		this.subscription = this.machine.state$.subscribe(state => {
			this.trackDuplicateBanner(!!state.context.duplicateError);
		});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onDobChanged(isoDate: string): void {
		this.parsedDateOfBirth = isoDate;
	}

	get headerKey(): string {
		return this.data.mode === 'editingItero' ? 'PatientEditor.EditTitle' : 'PatientEditor.CompleteTitle';
	}

	get isInvisalignMode(): boolean {
		return this.data.mode === 'fillingInvisalignFields';
	}

	get duplicateError(): Patient | null {
		return this.machine.state.context.duplicateError;
	}

	get isValid(): boolean {
		if (!this.form.firstName || !this.form.lastName) {
			return false;
		}
		if (this.isInvisalignMode && (!this.parsedDateOfBirth || !this.form.gender)) {
			return false;
		}
		if (this.dobFormat.isInvalidDob(this.parsedDateOfBirth)) {
			return false;
		}

		return true;
	}

	save(): void {
		const patient: Patient = {
			...this.data.patient,
			...this.form,
			dateOfBirth: this.parsedDateOfBirth,
			system: this.data.patient.system
		};

		this.machine.send({ type: 'EDITOR.SAVED', patient, createInvisalign: this.isInvisalignMode });
	}

	saveWithoutInvisalign(): void {
		const patient: Patient = {
			...this.data.patient,
			...this.form,
			dateOfBirth: this.parsedDateOfBirth,
			system: this.data.patient.system
		};

		this.machine.send({ type: 'EDITOR.SAVED_WITHOUT_INVISALIGN', patient });
	}

	cancel(): void {
		this.modalWindowRef.close(undefined);
	}

	private trackDuplicateBanner(visible: boolean): void {
		if (visible === this.duplicateBannerShown) {
			return;
		}

		this.duplicateBannerShown = visible;

		void this.bi.reporter?.trackStage({
			object: BI_DIMENSIONS.duplicateBanner,
			stage: visible ? 'shown' : 'hidden',
			previous: visible ? '' : 'shown',
			description: '',
			trigger: 'system',
			customAttributes: { feature: BI_FEATURE }
		});
	}
}
